/**
 * @module app.um
 */

import { autoinject, bindingMode, observable } from 'aurelia-framework';
import { ComponentBase, IForm } from 'app/shared/lib/essette-site';
import { bindable } from 'aurelia-typed-observable-plugin';
import { apiCall } from 'app/shared/lib/essette-api';
import { Toaster } from 'app/shared/lib/toaster';
import { AuthService } from 'app/um/services/auth-service';
import { getReference } from 'app/shared/models/essette-types';
import { I18N } from 'aurelia-i18n';
import { FlagOptions } from 'app/um/components/service-statuses/service-statuses';
import { deepClone } from 'app/shared/lib/utilities';
import { IApiSettings } from 'app/shared/lib/api/api-types';
import { guid } from 'app/shared/lib/guid';
import * as UM from 'app/um/models/essette-api';

enum EventOptions {
    initialStatus = 'Initial Status'
}

@autoinject()
export class SaveServiceStatus extends ComponentBase implements IForm {

    ////****************** Fields and Properties ************************************////
    @bindable.object({ defaultBindingMode: bindingMode.toView, changeHandler: 'reset' })
    public authServiceStatus: UM.AuthServiceStatus = { id: null };

    @bindable.array({ defaultBindingMode: bindingMode.toView })
    protected statuses: UM.AuthServiceStatus[] = [];

    @bindable.array({ defaultBindingMode: bindingMode.toView })
    protected determinationStatuses: UM.AuthStatus[] = [];

    @bindable.array({ defaultBindingMode: bindingMode.oneTime })
    private flagOptions: Array<string> = [];

    @bindable.array({ defaultBindingMode: bindingMode.oneTime })
    private allFlagOptions: Array<string> = [];

    @bindable.array({ defaultBindingMode: bindingMode.toView })
    private determinationFlags: string[] = [];

    public apiSettings: IApiSettings = {};

    @observable
    private addDetermination: boolean = false;

    private authServiceStatusId: string | null = null;
    // Value Bindings
    protected status: string = '';
    protected code: string = '';
    protected determinationStatus: UM.AuthStatus | null = null;
    protected flags: Array<string> = [];
    protected transitions: UM.AuthServiceStatus[] = [];
    protected exclusiveEvent: string | null = null;
    protected providerChangesRequestedEvent: boolean = false;
    protected id: guid = guid();
    protected isInitialStatus: boolean = false;

    // Drop Down Options
    private eventOptions: string[] = [];

    ////****************** Lifecycle events (in order of execution) *****************////

    constructor(
        private readonly service: AuthService,
        private readonly toaster: Toaster,
        private readonly i18n: I18N,
        ...rest: any[]) {
        super(...rest);
    }

    protected bind(): void {
        this.eventOptions = <string[]>Object.values(EventOptions);
        this.reset();
    }

    ////****************** Events ***************************************************////
    protected addDeterminationChanged(): void {
        if(!this.addDetermination && this.determinationStatus) {
            this.determinationStatus = null;
        }
        this.setDeterminationFlags();
    }

    ////****************** Functions ************************************************////

    // Set up certain flags based on Determination selection
    protected setDeterminationFlags(): void {
        // Certain  flags can only be selected if Add Determination is true
        if(this.addDetermination) {
            this.flagOptions.push(...this.determinationFlags.filter(flag => !this.flagOptions.includes(flag)));
        } else {
            this.flagOptions = this.allFlagOptions.filter(option => !this.determinationFlags.includes(option));
        }

        // If add determination is unchecked, filter out determination flags from currently selected
        if(!this.addDetermination && this.flags?.length) {
            this.flags = this.flags.filter(flag => !this.determinationFlags.includes(flag));
        }
    }

    // Build Auth Service Status to send over to the api
    protected buildAuthServiceStatus(): UM.AuthServiceStatus {
        let allowStatusDateEdit: boolean = false, allowNullDateOfService: boolean = false, isApproval: boolean = false,
            isOpen: boolean = false, isTurnAroundTimeBase: boolean = false, isVoided: boolean = false, sendToClaimsSystem: boolean = false;

        this.flags.forEach(flag => {
            switch(flag) {
                case FlagOptions.isApproval:
                    isApproval = true;
                    break;
                case FlagOptions.isOpen:
                    isOpen = true;
                    break;
                case FlagOptions.isTurnAroundTimeBase:
                    isTurnAroundTimeBase = true;
                    break;
                case FlagOptions.isVoided:
                    isVoided = true;
                    break;
                case FlagOptions.sendToClaimsSystem:
                    // Can only be set to true if add determination (aka isDetermination) is true
                    sendToClaimsSystem = this.addDetermination;
                    break;
            }
        });

        const returnTransitions = <Array<UM.AuthServiceStatus>>this.transitions.map(t => getReference(t));

        const returnAuthServiceStatus: UM.AuthServiceStatus = {
            id: this.authServiceStatus.id,
            description: this.status,
            code: this.code,
            determination: {
                isDetermination: this.addDetermination
            },
            flag: {
                // Status Flags
                allowStatusDateEdit: allowStatusDateEdit,
                allowNullDateOfService: allowNullDateOfService,
                isApproval: isApproval,
                isOpen: isOpen,
                isTurnAroundTimeBase: isTurnAroundTimeBase,
                isVoided: isVoided,
                sendToClaimsSystem: sendToClaimsSystem,
            },
            transitions: returnTransitions
        };
        if(this.addDetermination) returnAuthServiceStatus.determination = {
            ...returnAuthServiceStatus.determination,
            status: getReference(this.determinationStatus)
        };

        return returnAuthServiceStatus;
    }

    // Build Auth Service Status to send over to the api, exclusive to just status events
    protected buildAuthServiceStatusExclusive(): UM.AuthServiceStatus {
        const returnAuthServiceStatus: UM.AuthServiceStatus = {
            id: this.authServiceStatusId
        };

        switch(this.exclusiveEvent) {
            case EventOptions.initialStatus:
                returnAuthServiceStatus.flag = {
                    isInitial: true
                };
                break;
        }

        return returnAuthServiceStatus;
    }

    protected buildAuthServiceStatusEventsProviderChangesRequested(): UM.AuthServiceStatus {
        const returnAuthServiceStatus: UM.AuthServiceStatus = {
            id: this.authServiceStatusId,
            flag: {
                providerChangesRequested: true
            }
        };

        return returnAuthServiceStatus;
    }

    ////****************** IForm ****************************************************////

    public async reset(): Promise<void> {
        this.authServiceStatusId = this.authServiceStatus.id ?? null;
        this.status = this.authServiceStatus.description || '';
        this.code = this.authServiceStatus.code || '';
        this.addDetermination = !!this.authServiceStatus.determination?.isDetermination;
        this.determinationStatus = this.authServiceStatus.determination?.status || null;
        if(this.addDetermination && !this.determinationStatus && this.determinationStatuses.length > 0) this.determinationStatus = this.determinationStatuses[0]; // first entry is 'None'
        this.flags = [];
        this.transitions = [];
        this.exclusiveEvent = null;
        this.providerChangesRequestedEvent = false;

        const flags = this.authServiceStatus.flag;
        if(flags) {
            // Set Flags
            this.flagOptions.forEach(option => {
                if(
                    (option === FlagOptions.isApproval && flags.isApproval) ||
                    (option === FlagOptions.isOpen && flags.isOpen) ||
                    (option === FlagOptions.isTurnAroundTimeBase && flags.isTurnAroundTimeBase) ||
                    (option === FlagOptions.isVoided && flags.isVoided) ||
                    (option === FlagOptions.sendToClaimsSystem && flags.sendToClaimsSystem)
                ) {
                    this.flags.push(option);
                }
            });

            // Set Events
            if(flags.isInitial) {
                this.exclusiveEvent = EventOptions.initialStatus;
            }
            if(flags.providerChangesRequested) {
                this.providerChangesRequestedEvent = true;
            }
        }

        this.transitions = this.authServiceStatus.transitions ? deepClone(this.authServiceStatus.transitions) : [];
    }

    public hasChanges(): boolean {
        throw new Error('Method not implemented.');
    }

    public async submit(): Promise<boolean> {
        let isSuccessful = false;

        try {
            this.state.beginSave();

            // If an the Initial Service Status Event was deselected, we need to !updateExclusive first
            // to satisfy a potential addDetermination check constraint error
            if(!this.exclusiveEvent && this.isInitialStatus) {
                isSuccessful = await this.updateAuthServiceStatusEvents();
                this.isInitialStatus = false;
            }

            // Save Auth Service Status
            isSuccessful = await this.saveAuthServiceStatus();

            // If an event was selected, push an exclusive update
            if(this.exclusiveEvent || this.providerChangesRequestedEvent) {
                isSuccessful = await this.updateAuthServiceStatusEvents();
            }
        }
        catch(error) {
            throw error;
        }
        finally {
            this.toaster.standard.saved('um:service.status.$long', isSuccessful);
            this.state.endSave();
            return isSuccessful;
        }
    }

    ////****************** Service Calls ********************************************////

    // To Add/Update an Auth Service Status
    @apiCall
    protected async saveAuthServiceStatus(): Promise<boolean> {
        let isSuccessful = false;
        try {
            const response = await this.service.saveServiceStatus(this.buildAuthServiceStatus());
            if(!this.authServiceStatusId) {
                this.authServiceStatusId = response.id ?? null;
            }
            isSuccessful = response.isSuccessful;
        }
        catch(error) {
            // If Duplicate Status Code constraint violated, show toast
            if(error.status.errors) {
                const invalidCode = error.status.errors.find((err: any) => err.code === 'property.constraintViolated' && err.property === 'code');

                if(invalidCode) {
                    this.toaster.error().message(this.i18n.tr('ui:messages.duplicate.save_error', { what: this.i18n.tr('um:service.status.code.$long'), item: this.i18n.tr('um:service.status.$long') })).show();
                }
            }

            throw error;
        }
        finally {
            return isSuccessful;
        }
    }

    // To Update an Auth Service Status's Event information
    @apiCall
    protected async updateAuthServiceStatusEvents(): Promise<boolean> {
        let isSuccessful = false;
        if(this.authServiceStatusId) {
            try {
                const promises = [];
                if(this.exclusiveEvent) {
                    promises.push(this.service.updateServiceStatusExclusive(this.buildAuthServiceStatusExclusive()));
                }
                if(this.providerChangesRequestedEvent) {
                    promises.push(this.service.updateServiceStatusExclusive(this.buildAuthServiceStatusEventsProviderChangesRequested()));
                }
                const responses = await Promise.all(promises);
                isSuccessful = responses.every((response: any) => response.isSuccessful);
            }
            catch(error) {
                // If Status already has Event flag, show toast
                if(error.status.errors) {
                    const eventAlreadyFlagged = error.status.errors.find((err: any) => (err.description as string).startsWith('Auth Service Status is already flagged'));

                    if(eventAlreadyFlagged) {
                        this.toaster.error().message(this.i18n.tr('um:local.authServiceStatus.authServiceStatusEventChangeError')).show();
                    } else {
                        this.toaster.standard.saved('um:service.status.$long', isSuccessful);
                    }
                } else {
                    this.toaster.standard.saved('um:service.status.$long', false);
                }

                throw error;
            }
            finally {
                return isSuccessful;
            }
        }
        else {
            return isSuccessful;
        }
    }
}
